<template>
  <!-- No prepend-inner-icon, and the opener is a bare icon rather than a v-btn: this field sits
       in a md="2" column, where a leading icon plus a button's 40px hit area left about 100px
       for the text and clipped "21.09.2026 16:49" mid-time. The affixes are the first thing to
       give way — the value has to be readable. -->
  <v-text-field
    :model-value="display"
    :label="label"
    hide-details="auto"
    :error-messages="errorMessages"
    class="datetime-field"
    @update:model-value="draft = $event"
    @blur="commitTyped"
    @keydown.enter="commitTyped"
  >
    <!-- `close-on-content-click` off, or the first click on an hour would shut the menu.
         The pickers live inside the field's append slot so the whole control is one unit. -->
    <template #append-inner>
      <v-menu v-model="open" :close-on-content-click="false" location="bottom end" offset="8">
        <template v-slot:activator="{ props }">
          <v-icon v-bind="props" icon="mdi-calendar-clock" size="small" class="datetime-opener"
                  role="button" tabindex="0" aria-label="Datum und Uhrzeit auswählen"
                  @mousedown.prevent @keydown.enter.stop="open = !open" />
        </template>
        <v-card class="d-flex flex-wrap">
          <v-date-picker :model-value="dateValue" :min="minDate" hide-header show-adjacent-months
                         @update:model-value="onDate" />
          <!-- The whole point of this component: 24h regardless of the browser's locale.
               VTimePicker defaults to 'ampm' and ignores the app locale. -->
          <v-time-picker :model-value="timeValue" format="24hr" hide-header
                         @update:model-value="onTime" />
        </v-card>
      </v-menu>
    </template>
  </v-text-field>
</template>

<style scoped>
/* The opener still needs to look and behave like a control, just without a button's padding. */
.datetime-opener {
  cursor: pointer;
  opacity: var(--v-medium-emphasis-opacity);
}
.datetime-opener:hover,
.datetime-opener:focus-visible {
  opacity: 1;
}
/* The stamp is 16 characters and must not be clipped; at md="2" it is the widest thing in the
   row relative to its column. Shrinking it a touch buys the last few pixels without looking
   out of place beside the neighbouring fields. */
.datetime-field :deep(input) {
  font-size: 0.9375rem;
}
</style>

<script>
import { DateTime } from 'luxon'

// What travels in and out: the naked stamp the backend parses as Europe/Berlin. Kept to the
// byte — Connections.vue compares the query string against this value to decide whether a
// route change is its own, so "09:05" must not become "9:5".
const STAMP_FORMAT = "yyyy-MM-dd'T'HH:mm";
// What the user sees and types. The house format, same as DateTimeMixin renders everywhere else.
const DISPLAY_FORMAT = 'dd.MM.yyyy HH:mm';

export default {
  name: 'DateTimeField',

  props: {
    modelValue: { type: String, default: null },
    label: { type: String, required: true },
    // A full stamp, not a date: the caller computes it in Berlin time and we only ever need
    // its date half (see `minDate`). VTimePicker's own `min` is deliberately left unused —
    // it takes "HH:mm" with no notion of a day, so it would be right today and wrong on every
    // other date.
    min: { type: String, default: null },
  },

  emits: ['update:modelValue'],

  data() {
    return {
      open: false,
      // What is in the input while it is being typed. Null means "show the model value".
      draft: null,
      invalid: false,
    };
  },

  computed: {
    parsed() {
      if (!this.modelValue) {
        return null;
      }
      var value = DateTime.fromFormat(this.modelValue, STAMP_FORMAT);
      return value.isValid ? value : null;
    },

    display() {
      if (this.draft !== null) {
        return this.draft;
      }
      return this.parsed ? this.parsed.toFormat(DISPLAY_FORMAT) : '';
    },

    errorMessages() {
      return this.invalid ? ['Bitte als TT.MM.JJJJ HH:MM eingeben.'] : [];
    },

    // VDatePicker wants a Date. Building it from the parsed fields keeps it on the calendar
    // day the stamp names, whatever the browser's zone is.
    dateValue() {
      return this.parsed ? this.parsed.toJSDate() : null;
    },

    timeValue() {
      return this.parsed ? this.parsed.toFormat('HH:mm') : null;
    },

    minDate() {
      return this.min ? this.min.split('T')[0] : undefined;
    },
  },

  watch: {
    // A new value from outside (a shared link, the reset after a bad one) wins over whatever
    // half-typed text is still in the field.
    modelValue: function() {
      this.draft = null;
      this.invalid = false;
    },
  },

  methods: {
    // VDatePicker emits a native Date at local midnight. Reading its fields through Luxon
    // rather than toISOString() keeps the clicked day — the latter shifts it across UTC.
    onDate: function(value) {
      if (!value) {
        return;
      }
      var date = DateTime.fromJSDate(value);
      if (!date.isValid) {
        return;
      }
      this.emitParts(date.toFormat('yyyy-MM-dd'), this.timeValue || '00:00');
    },

    // Always "HH:mm" in 24h, whatever `format` displays. Fires as soon as hour and minute are
    // both set, so several times per interaction — harmless, each emit is a complete value.
    onTime: function(value) {
      if (!value) {
        return;
      }
      var date = this.parsed ? this.parsed.toFormat('yyyy-MM-dd') : DateTime.now().toFormat('yyyy-MM-dd');
      this.emitParts(date, value);
    },

    emitParts: function(date, time) {
      this.draft = null;
      this.invalid = false;
      this.$emit('update:modelValue', `${date}T${time}`);
    },

    // Typing stays possible — it is faster than three clicks for anyone who knows the time
    // they want. An unparsable draft is flagged rather than silently dropped, and the field
    // keeps showing what the user wrote so they can correct it.
    commitTyped: function() {
      if (this.draft === null) {
        return;
      }
      var typed = DateTime.fromFormat(this.draft.trim(), DISPLAY_FORMAT);
      if (!typed.isValid) {
        this.invalid = true;
        return;
      }
      this.draft = null;
      this.invalid = false;
      this.$emit('update:modelValue', typed.toFormat(STAMP_FORMAT));
    },
  },
};
</script>
